<script setup lang="ts">
  import type { EnumValues } from '@travelata-libs/utils';
  import { ref } from 'vue';
  import { PillColor, PillAppearance } from './Pill.types';
  import PillBase from './PillBase.vue';
  import { type IconName, type Typo, Tooltip } from '@/components';

  interface PillProps {
    appearance?: EnumValues<typeof PillAppearance>;
    color?: EnumValues<typeof PillColor>;
    iconLeft?: IconName;
    iconRight?: IconName;
  }

  withDefaults(defineProps<PillProps>(), {
    appearance: PillAppearance.Primary,
    color: PillColor.Sky,
    iconLeft: undefined,
    iconRight: undefined,
  });

  const pillBaseRef = ref<typeof Typo | null>(null);
  const tooltipRef = ref<typeof Tooltip | null>(null);
  const shouldAddTooltip = ref(false);

  const checkOverflow = (element) => {
    return element.scrollWidth > element.clientWidth;
  };

  const onHover = () => {
    const textElement = pillBaseRef.value?.$refs.typoElement.$el;
    shouldAddTooltip.value = checkOverflow(textElement);
  };
</script>

<template>
  <div class="tl-pill__wrap">
    <template v-if="shouldAddTooltip">
      <Tooltip ref="tooltipRef" trigger="hover" placement="top">
        <template #reference>
          <PillBase v-bind="$props" ref="pillBaseRef" @mouseover.native="onHover">
            <template #prepend>
              <slot name="prepend" />
            </template>
            <slot />
            <template #append>
              <slot name="append" />
            </template>
          </PillBase>
        </template>

        <template #default>
          <slot />
        </template>
      </Tooltip>
    </template>

    <template v-else>
      <PillBase v-bind="$props" ref="pillBaseRef" @mouseover.native="onHover">
        <template #prepend>
          <slot name="prepend" />
        </template>
        <slot />
        <template #append>
          <slot name="append" />
        </template>
      </PillBase>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .tl-pill__wrap {
    max-width: 100%;
  }
</style>
